import { useState, useEffect } from "react";
import { login, passwordlessStart, passwordlessFinish } from "../api";
// 83 §2 — shape (c) runs the SAME ceremony helper as shape (b); what differs
// is only that the server sent an empty `allowCredentials`.
import { assertPasskey, passkeysAvailable, classifyFailure } from "../lib/webauthnClient";
// 83 §2 — the name AUTH-6 will not let lie: the permissive variant never says
// "passwordless", and the choice is made by the resolver rather than here.
import { passwordlessTextKeys } from "../lib/authShapes";
import { useTranslation } from "../i18n";
import { CT, RAD, FS } from "../lib/tokens";
// 77 §4a — the demo warning belongs BEFORE sign-in, not only after it.
import DemoBanner from "../components/DemoBanner";
// 78 §4 — THE modal. One of them, for every login shape there will ever be.
import AuthStepModal from "../components/AuthStepModal";
// 83 §3 — the door for somebody with no password. Rendered INSTEAD of the
// sign-in form when the URL carries an invitation, because a person who has
// never had a credential has nothing to type into this screen.
import InvitationView from "./InvitationView";
import type { LoginResponse } from "../api";

export default function LoginView({
  onLogin,
  firmName = "Legal Practice Manager",
  firmLogo = null,
}: any) {
  const { t, lang, chooseLang, supported } = useTranslation();
  // 83 §3 — `?invite=<token>` is the link in the invitation. Read ONCE, from the
  // URL as it was when this mounted: the token is not stored anywhere, so
  // closing the tab loses nothing that was not already in the mail, and nothing
  // is spent by arriving here (see InvitationView).
  const [inviteToken] = useState<string | null>(() => {
    try {
      return new URLSearchParams(window.location.search).get("invite");
    } catch {
      return null;
    }
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  // 83 §2 — the QUIET channel. `err` is the red box; this is for the one
  // passkey outcome the ruling forbids shouting about (see rule (ii) below).
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState<any>(null);
  const [showPw, setShowPw] = useState(false);
  // 78 §1 — what the server hands back INSTEAD of a session when a second step
  // is owed. Holding it in state rather than navigating keeps the whole
  // sign-in on one screen, which is what makes "one modal" readable.
  const [step2, setStep2] = useState<LoginResponse | null>(null);

  useEffect(() => {
    fetch("/api/auth/config", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) =>
        setCfg(
          c || {
            authMethodsEnabled: ["microsoft"],
            defaultAuthMethod: "microsoft",
            m365Configured: false,
          },
        ),
      )
      .catch(() =>
        setCfg({
          authMethodsEnabled: [],
          defaultAuthMethod: "password",
          m365Configured: false,
        }),
      );
  }, []);

  const submit = async (e: any) => {
    e.preventDefault();
    setErr("");
    setHint("");
    // RULED by Laurent 2026-08-04 (brief 37 §2): BROWSER-NATIVE VALIDATION IS
    // OURS WHEN OUR MARKUP TRIGGERS IT. `required` on these two inputs, and
    // `type="email"`, made the BROWSER render its own words in the BROWSER's UI
    // language — on the one screen a user meets before this app has a language
    // at all, and the one screen where the rest of the app's toasts and t() do
    // not reach. Not setCustomValidity: that keeps a browser-owned rendering
    // path alive for one screen while everything else already speaks through
    // the dictionary. So the form is `noValidate` and these three checks say
    // the same three things, from the dictionary, into the error box this
    // screen already has. True browser/OS chrome — print dialog, file picker,
    // permission prompts — stays out of scope; the boundary is what OUR markup
    // triggers.
    const addr = email.trim();
    if (!addr) {
      setErr(t("login.email_required", { fallback: "Email required" }));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
      setErr(t("login.email_invalid", { fallback: "Enter a valid email address" }));
      return;
    }
    if (!password) {
      setErr(t("login.password_required", { fallback: "Password required" }));
      return;
    }
    setBusy(true);
    try {
      const res = await login(addr, password);
      // 78 §1 — THE INVARIANT, where this screen meets it. A response with no
      // token is not a failure and not a session: it is a challenge, and the
      // modal is the only way past it. `login()` (src/api.ts) has already
      // declined to store anything, so nothing here is holding a credential.
      if (res && !res.token && (res.mfaRequired || res.enrolmentRequired)) {
        setStep2(res);
        return;
      }
      onLogin?.(res.user, res.token);
    } catch (ex: any) {
      // Phase M+3.7 — server emits a stable `code` in the error body so we
      // branch on identifier rather than English substring. The api.js
      // wrapper attaches `.code` and `.body` to thrown Errors. The substring
      // check on `Too many` stays as a backward-compat fallback for
      // pre-M+3.7 server builds.
      const msg = String(ex?.message || "");
      const code = ex?.code || "";
      const isTooMany =
        code === "auth.too_many_attempts" ||
        code === "auth.too_many_for_account" ||
        msg.includes("Too many");
      // 75 §2/§3 — a suspended or archived account is refused AFTER the
      // password verified (a 403, so the code survives api.ts's 401 wipe),
      // and the one thing this screen must not tell that person is "Invalid
      // credentials" — the credentials were right.
      const isDisabled = code === "auth.account_disabled";
      // 77 §4b — an EXPIRED credential gets its own sentence, for the same
      // reason a disabled account does and one degree more sharply: "an expired
      // demo login that looks like a typo is a support call". It must never
      // fall through to invalid_credentials.
      const isExpiredCred = code === "auth.credential_expired";
      setErr(
        isTooMany
          ? msg
          : isDisabled
            ? t("login.account_disabled", {
                fallback:
                  "This account has been suspended or archived. Contact your firm's administrator.",
              })
            : isExpiredCred
              ? t("login.credential_expired", {
                  fallback:
                    "This login has expired. Contact your firm's administrator to have it renewed.",
                })
              : t("login.invalid_credentials", { fallback: "Invalid credentials" }),
      );
    } finally {
      setBusy(false);
    }
  };

  // ── 83 §2 — SHAPE (c): SIGN IN WITH THE PASSKEY AND TYPE NOTHING ──────────
  //
  // A SECOND PRIMARY, not a link and not a secondary. `[RULED — Laurent,
  // 2026-08-15, on 82a's glance]` "the rule forbids a control MORE PROMINENT
  // than the submit doing something else — it does not forbid two primaries.
  // Where a screen has two genuine ways in, they are PEERS, and each gets its
  // own primary submit directly beneath the thing it acts on."
  //
  // This one acts on NOTHING TYPED, which is the whole shape, so it stands in
  // its own block below the form rather than inside it. It is never rendered
  // unless the tenant permits the shape (`/api/auth/config`).
  const passwordlessOffered = cfg?.passwordless === true && passkeysAvailable();
  const passwordlessMode = cfg?.passwordlessMode === "permissive" ? "permissive" : "strict";

  const signInWithPasskey = async () => {
    setErr("");
    setHint("");
    setBusy(true);
    try {
      const start = await passwordlessStart();
      const assertion = await assertPasskey(start.webauthn);
      const res = await passwordlessFinish(start.challenge, assertion);
      onLogin?.(res.user, res.token);
    } catch (ex: unknown) {
      // 82a's glance, rule (i): NEVER SURFACE A `DOMException`. Every failure
      // maps to a written sentence; `classifyFailure` owns that mapping and
      // `PASSKEY_MESSAGE_KEYS` is what the lint counts. Rule (ii): a plain
      // cancel must be QUIET — no red box for a person who did nothing wrong.
      //
      // `unknown` rather than `any`, and narrowed here: the ratchet may shrink
      // and never grow, and re-recording it is for a file MOVE. `api` attaches
      // `.code` to the errors it throws, and a DOMException from the ceremony
      // has none — which is exactly the fork below.
      const code = typeof ex === "object" && ex !== null && "code" in ex
        ? String((ex as { code?: unknown }).code ?? "")
        : "";
      if (code === "auth.account_disabled") {
        setErr(t("login.account_disabled", {
          fallback: "This account has been suspended or archived. Contact your firm's administrator.",
        }));
      } else if (code === "auth.access_cut") {
        setErr(t("login.access_cut", {
          fallback: "Access to this account has been suspended by an administrator.",
        }));
      } else if (code === "auth.passkey_unregistered") {
        // 83 — THE LOCAL CEREMONY SUCCEEDING AND THE SERVER REFUSING IS NORMAL
        // AND MUST BE SAID (82i). The browser still holds the credential and
        // Touch ID still accepts it — a site cannot delete a passkey from Apple
        // Passwords, only the person can — so the screen must explain that the
        // FIRM no longer knows it, rather than implying the person got something
        // wrong. It arrives as a 403 precisely so this code survives api.ts.
        setErr(t("login.passkey_unregistered", {
          fallback:
            "This passkey is no longer registered here — it was removed. Your device still offers it, which is normal. Sign in another way, then set up a new one.",
        }));
      } else if (code === "auth.invalid_credential" || code === "auth.challenge_expired") {
        setErr(t("login.passkey_not_accepted", {
          fallback: "That passkey was not accepted. Try again, or sign in with your password.",
        }));
      } else {
        const failure = classifyFailure(ex);
        // 82a's glance, rule (ii): YOU CANNOT DETECT ABSENCE, SO DO NOT TRY.
        // `not_completed` deliberately covers BOTH "he cancelled" and "nothing
        // could answer", because telling them apart would be a fingerprinting
        // channel. So it is one message that says what to do NEXT — and it goes
        // in the QUIET box, not the red one: "a plain cancel must be quiet, no
        // red box for a person who did nothing wrong." Everything else is a
        // real failure and keeps the red box.
        if (failure === "not_completed") {
          setHint(t("login.passkey_not_completed", {
            fallback: "No passkey was used. Try again, or sign in with your password.",
          }));
        } else {
          setErr(t(`auth.passkey.${failure}`, {
            fallback: "Your passkey could not be used. Try again, or sign in with your password.",
          }));
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const msEnabled =
    cfg?.authMethodsEnabled?.includes("microsoft") && cfg?.m365Configured;
  const pwEnabled = true; // always available as fallback

  if (inviteToken) {
    return <InvitationView token={inviteToken} onLogin={onLogin} firmName={firmName} />;
  }

  if (step2) {
    return (
      <AuthStepModal
        mode={step2.enrolmentRequired ? "enrol" : "challenge"}
        challenge={step2.challenge}
        shapes={step2.shapes || []}
        recoveryRemaining={step2.recoveryCodes || null}
        // 82 §4 — the assertion options ride on the same response that carried
        // the challenge, so the modal has everything it needs to ask the
        // authenticator without a second round trip.
        webauthn={step2.webauthn || null}
        // 83 §5 — the server's own name for this browser, pre-filling the
        // trust box so there is no second `labelFor` on the client.
        suggestedDeviceName={step2.suggestedDeviceName || null}
        cfg={cfg}
        onDone={(user, token) => {
          setStep2(null);
          onLogin?.(user, token);
        }}
        onCancel={() => {
          // Nothing to discard: no token was ever stored. Back to the form.
          setStep2(null);
          setPassword("");
        }}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `var(--bd, ${CT.c_0f1115})`,
        fontFamily: "'DM Sans', sans-serif",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          background: `var(--bc, ${CT.c_1a1d24})`,
          border: `1px solid var(--br, ${CT.c_2a2e37})`,
          borderRadius: RAD.r12,
          padding: 28,
          boxShadow: "0 10px 40px rgba(0,0,0,.5)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* ── 82m — THE LANGUAGE CONTROL, AND WHY IT IS AT THE TOP ─────────
            82l made the browser's own preference the default, which is right
            for nearly everybody and invisible. This is for the rest: a shared
            machine, an English-configured Mac in a Brussels office, a Dutch
            speaker sitting at a French colleague's desk. The browser can be
            wrong, and when it is there has to be a way to say so.

            ABOVE EVERYTHING ELSE ON THE CARD, because a person who cannot read
            this screen needs it BEFORE they read the rest of it — a switcher
            below the form is only findable by someone who did not need it.

            It writes `lx:lang` and nothing else. `api.ts:80`'s logout sweep
            spares that key by name, so a choice made at the door survives
            signing out — which is the point of making it here rather than in
            Settings, where you must already be inside. */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
          {supported.map((code: string) => (
            <button
              key={code}
              type="button"
              onClick={() => chooseLang(code)}
              // The endonym, from the catalogue. A language names itself in
              // its OWN language — somebody looking for Dutch looks for
              // "Nederlands" — so `lang.name.nl` carries the same text in all
              // three locales. That is a statement, not a duplication, and it
              // keeps these out of the prose-map gate's untranslated-literal
              // class, which was right to flag them: they reach a human.
              aria-label={t(`lang.name.${code}`, { fallback: code.toUpperCase() })}
              aria-pressed={lang === code}
              title={t(`lang.name.${code}`, { fallback: code.toUpperCase() })}
              style={{
                padding: "3px 8px",
                background: lang === code ? `var(--ac, ${CT.c_4a7cff})` : "transparent",
                color: lang === code ? CT.c_fff : `var(--tm, ${CT.c_9ca3af})`,
                border: `1px solid ${lang === code ? "transparent" : `var(--br, ${CT.c_2a2e37})`}`,
                borderRadius: RAD.r6,
                fontSize: FS.s11,
                fontWeight: 600,
                letterSpacing: ".04em",
                cursor: "pointer",
              }}
            >
              {code.toUpperCase()}
            </button>
          ))}
        </div>
        {/* 77 §4a — a tenant on the lenient floor says so, and says it here
            because this is the screen somebody reads before typing anything. */}
        {cfg?.demo === true && <DemoBanner position="static" />}
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          {firmLogo && (
            <img
              src={firmLogo}
              alt=""
              style={{
                maxHeight: 48,
                maxWidth: 180,
                objectFit: "contain",
                marginBottom: 10,
              }}
            />
          )}
          <h1
            style={{
              fontSize: FS.s24,
              fontWeight: 700,
              color: `var(--tp, ${CT.c_fff})`,
              margin: 0,
            }}
          >
            {firmName}
          </h1>
          <p
            style={{
              fontSize: FS.s12,
              color: `var(--tm, ${CT.c_9ca3af})`,
              margin: "6px 0 0",
            }}
          >
            {t("login.sign_in", { fallback: "Sign in to continue" })}
          </p>
        </div>

        {msEnabled && (
          <button
            type="button"
            onClick={() => {
              window.location.href = "/api/auth/microsoft/login-start";
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "11px 14px",
              background: CT.c_2f2f2f,
              color: CT.c_fff,
              border: `1px solid ${CT.c_3a3a3a}`,
              borderRadius: RAD.r8,
              fontSize: FS.s13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
            title={t("login.signin_microsoft_title", {
              fallback: "Sign in with your Microsoft 365 account",
            })}
          >
            <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill={CT.c_f25022} />
              <rect x="12" y="1" width="10" height="10" fill={CT.c_7fba00} />
              <rect x="1" y="12" width="10" height="10" fill={CT.c_00a4ef} />
              <rect x="12" y="12" width="10" height="10" fill={CT.c_ffb900} />
            </svg>
            {t("login.signin_microsoft", {
              fallback: "Sign in with Microsoft",
            })}
          </button>
        )}

        {/*
          83 — SHAPE (c) STANDS ABOVE THE FORM, NOT UNDER IT.

          On the glance it sat below a filled-in email and read as "sign in with
          THAT account's passkey". It is not: it is a DISCOVERABLE sign-in that
          never sees the address — `allowCredentials` is empty and the account is
          found from whatever credential the authenticator offers. A control that
          ignores the field above it must not be placed under that field.

          So it is a peer of the Microsoft button, before anything is typed, and
          it says in one line that no address is needed. The 2026-08-15 ruling
          permits two primaries where a screen has two genuine ways in; this is
          the same two, ordered so neither reads as an operation on the other's
          input.
        */}
        {passwordlessOffered && (
          <>
            <button
              type="button"
              onClick={signInWithPasskey}
              disabled={busy}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "10px 14px",
                background: `var(--ac, ${CT.c_4a7cff})`,
                color: CT.c_fff,
                border: "none",
                borderRadius: RAD.r8,
                fontSize: FS.s13,
                fontWeight: 600,
                cursor: busy ? "wait" : "pointer",
                fontFamily: "inherit",
                opacity: busy ? 0.7 : 1,
                width: "100%",
              }}
            >
              {t(passwordlessTextKeys(passwordlessMode)!.nameKey, {
                fallback: "Sign in with a passkey",
              })}
            </button>
            <p style={{ fontSize: FS.s11, color: `var(--tm, ${CT.c_9ca3af})`, margin: "6px 0 0", lineHeight: 1.5 }}>
              {t("login.passkey_no_address", {
                fallback: "No email address needed — your device chooses the account.",
              })}
            </p>
            {hint && (
              <div style={{ color: `var(--ts, ${CT.c_8892a4})`, fontSize: FS.s12, padding: "6px 0 0" }}>
                {hint}
              </div>
            )}
          </>
        )}

        {/*
          83 — THE "or" ONLY EXISTS IF SOMETHING PRECEDES IT.

          On the glance it rendered with nothing above it: staging has
          `m365Configured: false`, so there was no first option for it to be an
          alternative TO, and the form below it was showing anyway. A divider
          between one thing and nothing is a line that says a choice was made
          when none was offered.
        */}
        {(msEnabled || passwordlessOffered) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              margin: "14px 0 4px",
              color: `var(--ts, ${CT.c_8892a4})`,
              fontSize: FS.s12,
            }}
          >
            <span style={{ flex: 1, height: 1, background: `var(--br, ${CT.c_2a2e37})` }} />
            {t("login.or", { fallback: "or" })}
            <span style={{ flex: 1, height: 1, background: `var(--br, ${CT.c_2a2e37})` }} />
          </div>
        )}

        {/* The toggle is only a toggle when there is something to toggle AWAY
            from. With no Microsoft button the form renders unconditionally
            below, so this would have hidden nothing and offered nothing. */}
        {msEnabled && (
        <button
          type="button"
          onClick={() => setShowPw((v) => !v)}
          style={{
            background: "none",
            border: "none",
            color: `var(--tm, ${CT.c_9ca3af})`,
            fontSize: FS.s11,
            cursor: "pointer",
            padding: 2,
            fontFamily: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: 2,
          }}
        >
          {showPw
            ? t("common.hide", { fallback: "Hide" })
            : t("login.or_password", {
                fallback: "Or sign in with email and password",
              })}
        </button>
        )}

        {(showPw || !msEnabled) && pwEnabled && (
          <form
            onSubmit={submit}
            // `noValidate` and not merely dropping `required`: `type="email"`
            // below ALSO makes the browser speak (and it is kept, because it is
            // what gives a phone the right keyboard). One attribute closes the
            // whole browser-owned path; `submit` says the words instead.
            noValidate
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span
                style={{
                  fontSize: FS.s11,
                  color: `var(--ts, ${CT.c_d1d5db})`,
                  fontWeight: 500,
                }}
              >
                {t("login.email", { fallback: "Email" })}
              </span>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span
                style={{
                  fontSize: FS.s11,
                  color: `var(--ts, ${CT.c_d1d5db})`,
                  fontWeight: 500,
                }}
              >
                {t("login.password", { fallback: "Password" })}
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
              />
            </label>
            {err && (
              <div
                style={{
                  fontSize: FS.s12,
                  color: `var(--rd, ${CT.c_f87171})`,
                  background: "rgba(248,113,113,.08)",
                  border: "1px solid rgba(248,113,113,.3)",
                  borderRadius: RAD.r6,
                  padding: "8px 10px",
                }}
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              style={{
                marginTop: 2,
                padding: "10px 14px",
                background: `var(--ac, ${CT.c_4a7cff})`,
                color: CT.c_fff,
                border: "none",
                borderRadius: RAD.r8,
                fontSize: FS.s13,
                fontWeight: 600,
                cursor: busy ? "wait" : "pointer",
                fontFamily: "inherit",
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy
                ? t("login.signing_in", { fallback: "Signing in…" })
                : t("login.signin", { fallback: "Sign in" })}
            </button>

          </form>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "9px 11px",
  background: `var(--bd, ${CT.c_0f1115})`,
  color: `var(--tp, ${CT.c_fff})`,
  border: `1px solid var(--br, ${CT.c_2a2e37})`,
  borderRadius: RAD.r7,
  fontSize: FS.s13,
  fontFamily: "inherit",
  outline: "none",
};
